import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useUserContext } from './user';
import { useThemeContext } from './theme';
import endpoint, { ErrorResponse } from 'utils/endpoint';
import { trackEvent } from 'utils/google-tag-manager';
import { useRouter } from 'next/router';
import { AREA_LISTINGS_ROUTE, getRouteNameFromPath, ADDRESS_ROUTE } from 'components/dynamic-page/route-matchers';
import {
  GTM_CLICK_ADDRESS_PAGE_FAVOURITE_ICON,
  GTM_CLICK_FAV_ON_AREA_PAGE,
  GTM_CLICK_FAV_ON_MAP,
  GTM_CLICK_FAV_ON_MODAL,
} from '../constants/events';
import Listing from 'data/listing';
import { AnalyticsController } from 'analytics/analytics-controller';
import { useTenantId } from 'hooks/use-tenant-id';

import { PROPERTY_FAVOURITED_ACTION_ADDED, PROPERTY_FAVOURITED_ACTION_REMOVED, PROPERTY_FAVOURITED_SOURCE_OTHER } from '@bedrock/analytics';
import type { PropertyFavouritedSource } from '@bedrock/analytics';
import type { StandardStatusString } from 'types/reso';
import { toBedrockStandardStatus, toBedrockTenantId } from 'analytics/providers/kinesis/adapters';
import type { SavedListingsParams, Meta } from 'utils/types';
import { withTryAsync } from 'utils/withTry';

const API_V4_BASE_URL = '/services/api/v4/saved-listings';
const API_V4_LISTING_IDS_URL = `${API_V4_BASE_URL}/listing-ids`;

const EMPTY_SAVED_LISTINGS: SavedListingListItem[] = [];

export type SavedListingItem = {
  id: string;
  type: 'saved-listings';
  addressSlug: string;
  listing_id?: string;
};

// --- New Nested Response Types --- START ---
// Type for the attributes of a nested saved listing
type NestedSavedListingAttributes = {
  id: number;
  userId: number;
  source: string;
  isSaved: boolean;
  createdAt: string;
  updatedOn: string;
  addressSlug: string | null; // Can be null
  listingId: number | null; // Can be null
  listing: Record<string, any> | null; // Embedded listing attributes
};

// Type for a single item in the new nested response data array
export type NestedSavedListingDataItem = {
  id: string;
  type: 'nested-saved-listings';
  attributes: NestedSavedListingAttributes;
};

// Type for the overall nested response
export type NestedSavedListingsResponse = {
  data: NestedSavedListingDataItem[];
  meta: Meta;
};
// --- New Nested Response Types --- END ---

// Enhanced type for full listing data (remains the same, populated differently)
export type SavedListingFull = {
  id: string;
  type: 'saved-listings-full';
  addressSlug: string | null;
  listingId: number;
  listing?: Listing;
  isOriginalListing: boolean;
  hasActiveListing: boolean;
};

/**
 * The identifiers of one saved listing, which is all `isSavedListing` needs.
 * Both are kept: a card may not have the slug, and a relisted property keeps
 * its slug but gets a new listing id.
 */
export type SavedListingListItem = {
  addressSlug: string | null;
  listingId: number;
};

type SavedListingIdsResponse = {
  data: SavedListingListItem[];
  meta?: { totalCount?: number };
};

/** Builds full (listing-embedded) saved-listing items from a raw nested response's data. */
function buildSavedListingsFull(data: NestedSavedListingDataItem[]): SavedListingFull[] {
  return data.map(nestedSavedListing => {
    const { attributes } = nestedSavedListing;
    let listing: Listing | undefined;
    let hasActiveListing = false;
    let isOriginal = true;

    if (attributes.listing) {
      try {
        listing = Listing.fromClientAppListing({
          type: 'listings',
          attributes: attributes.listing,
          // Pass id separately if constructor expects it
          id: attributes.listing.id?.toString() || '',
        });

        hasActiveListing = listing.isAvailable;
        isOriginal = attributes.listingId === listing.id;
      } catch (e) {
        console.error(`Error processing listing object for saved listing ID ${nestedSavedListing.id}:`, e);
        listing = undefined;
      }
    }

    return {
      id: nestedSavedListing.id,
      type: 'saved-listings-full' as const,
      addressSlug: attributes.addressSlug,
      // A row can carry a null `listingId` while still embedding the listing,
      // which would otherwise leave this item with no id at all. Only fills a
      // missing one — where both exist and differ, `listingId` is the listing
      // that was saved and the embed is what replaced it (`isOriginalListing`).
      listingId: attributes.listingId ?? attributes.listing?.id,
      listing,
      hasActiveListing,
      isOriginalListing: isOriginal,
    };
  });
}

export type AddSavedListingProps = {
  listingId: number;
  isModal?: boolean;
  /** Optional when caller has it (e.g. address page/modal) for richer Kinesis payload. */
  standard_status?: StandardStatusString;
  /** Optional override for URL in analytics; defaults to current asPath. */
  url?: string;
  /** Optional source for Kinesis (e.g. ADDRESS_PAGE, MAP_PIN); defaults to OTHER when not provided. */
  source?: PropertyFavouritedSource;
};

export type RemoveSavedListingOptions = {
  /** Optional source for Kinesis (e.g. ADDRESS_PAGE, MAP_PIN); defaults to OTHER when not provided. */
  source?: PropertyFavouritedSource;
};

export interface ISavedListingContext {
  savedListings: SavedListingListItem[];
  /**
   * Both of these resolve when the change is complete. For adding, that
   * includes the list refetch that runs afterwards. A caller can await them to
   * keep a button disabled until the favourite state is final. Callers that do
   * not need that can ignore the returned promise.
   */
  addSavedListing: (props: AddSavedListingProps) => Promise<void>;
  removeSavedListing: (listingId: number, options?: RemoveSavedListingOptions) => Promise<void>;
  isSavedListing: (addressSlug?: string, listingId?: number) => boolean;
  /**
   * Fetches one page of saved listings. Resolves rather than rejects on
   * failure, so `error` is the only way to tell a failed request from a user
   * with no saved listings — `savedListings` is empty and `meta` unpopulated
   * in both cases.
   */
  getSavedListings: (params: SavedListingsParams) => Promise<{ savedListings: SavedListingFull[]; meta: Meta; error?: ErrorResponse }>;
  /**
   * Folds items fetched via `getSavedListings` into `savedListings`. The
   * saved check is hydrated from the full list already, so this only matters
   * for a listing saved in another tab or session since that fetch.
   */
  mergeSavedListings: (items: readonly SavedListingFull[]) => void;
}

export const SavedListingContext = createContext<ISavedListingContext | Record<string, unknown>>({});

export function useSavedListingContext <T extends ISavedListingContext | Record<string, unknown> = ISavedListingContext>() {
  return useContext(SavedListingContext) as T;
}

interface Props {
  children: ReactNode;
}


export default function SavedListingContextProvider({ children }: Props) {
  const { isAuthenticated } = useUserContext();
  const { themeName } = useThemeContext();
  const tenantId = useTenantId();
  const [savedListings, setSavedListings] = useState<SavedListingListItem[]>([]);
  const { asPath } = useRouter();

  // Create a map of addressSlug -> true for quick lookups
  const savedListingSlugMap = useMemo(() => {
    // Filter out items with null addressSlug before creating the map
    return new Map(savedListings.filter(item => item.addressSlug).map(item => [item.addressSlug, true]));
  }, [savedListings]);

  /**
   * The ids of the saved listings. `isSavedListing` searches this when a slug
   * does not match any saved row.
   *
   * This is a Set instead of a search through the array, because that lookup
   * happens often. Some listing cards have no slug, so those cards always use
   * it. It also runs during render, once per card, and the array grows as the
   * user views more pages of favourites. Searching the array would take
   * (number of cards x number of favourites) comparisons per render.
   *
   * Ids that are not numbers are excluded. A row with no id and a card with no
   * id both convert to `NaN`. A Set treats `NaN` as equal to `NaN`, but the
   * previous comparison did not. Excluding them keeps the previous result.
   */
  const savedListingIdSet = useMemo(() => {
    const ids = new Set<number>();
    for (const item of savedListings) {
      const id = Number(item.listingId);
      if (Number.isFinite(id)) {
        ids.add(id);
      }
    }
    return ids;
  }, [savedListings]);

  // Pure fetch: returns the identifiers of every saved listing (or null on
  // error). Callers decide when to setState.
  const fetchSavedListings = async (): Promise<SavedListingListItem[] | null> => {
    const [response, error] = await withTryAsync<SavedListingIdsResponse, ErrorResponse>(endpoint)(API_V4_LISTING_IDS_URL, 'GET', {
      filter: {
        source: themeName,
      },
    });

    if (error) {
      console.error('Failed to sync saved listings:', error);
      return null;
    }
    if (!response?.data) {
      console.warn('syncSavedListing received successful response, but no data found.');
      return null;
    }
    return response.data.map(item => ({
      addressSlug: item.addressSlug,
      listingId: Number(item.listingId),
    }));
  };

  const syncSavedListing = async () => {
    const items = await fetchSavedListings();
    if (items) setSavedListings(items);
  };

  useEffect(() => {
    if (isAuthenticated) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      syncSavedListing();
    } else if (savedListings.length) {
      setSavedListings(EMPTY_SAVED_LISTINGS);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const addSavedListing: ISavedListingContext['addSavedListing'] = async (props: AddSavedListingProps) => {
    const { listingId, isModal } = props;
    const routeName = getRouteNameFromPath(asPath);
    const isOnAreaPage = routeName === AREA_LISTINGS_ROUTE;
    const isOnAddressPage = routeName === ADDRESS_ROUTE;
    const isOnMapPage = asPath === '/search';

    if (isModal) {
      trackEvent(GTM_CLICK_FAV_ON_MODAL);
    } else if (isOnAreaPage) {
      trackEvent(GTM_CLICK_FAV_ON_AREA_PAGE);
    } else if (isOnAddressPage) {
      trackEvent(GTM_CLICK_ADDRESS_PAGE_FAVOURITE_ICON);
    } else if (isOnMapPage) {
      trackEvent(GTM_CLICK_FAV_ON_MAP);
    }

    // The slug is not sent. The server resolves it from the listing id, so it
    // is only known here once the response comes back.
    setSavedListings(prev => {
      if (prev.some(item => Number(item.listingId) === Number(listingId))) {
        return prev;
      }
      return [...prev, { addressSlug: null, listingId }];
    });

    const [response, error] = await withTryAsync<SavedListingItem, ErrorResponse>(endpoint)(API_V4_BASE_URL, 'POST', {
      listingId,
      source: themeName,
    });

    if (error) {
      console.error('Failed to add saved listing:', error);
      setSavedListings(prev => prev.filter(item => Number(item.listingId) !== Number(listingId)));
    } else if (response) {
      const newSavedListing: SavedListingListItem = {
        addressSlug: response.addressSlug,
        listingId,
      };

      setSavedListings(prev => [
        ...prev.filter(item => Number(item.listingId) !== Number(listingId)),
        newSavedListing,
      ]);

      // Kinesis: favourite-added event. Sent before the refetch below, so that
      // awaiting this function does not delay the event.
      AnalyticsController.trackPropertyFavourited(
        {
          listing_id: String(listingId),
          address_slug: response.addressSlug ?? '',
        },
        {
          action: PROPERTY_FAVOURITED_ACTION_ADDED,
          source: props.source ?? PROPERTY_FAVOURITED_SOURCE_OTHER,
          url: props.url ?? asPath,
          tenant_id: toBedrockTenantId(tenantId),
          ...(props.standard_status !== undefined && { standard_status: toBedrockStandardStatus(props.standard_status) }),
        }
      );

      // Awaited, so this function only resolves once the list is up to date.
      // The refetch returns every favourite, so it can replace state outright.
      await syncSavedListing();
    }
  };

  const removeSavedListing: ISavedListingContext['removeSavedListing'] = async (listingId, options) => {
    const slugBeforeRemove = savedListings.find(item => Number(item.listingId) === Number(listingId))?.addressSlug;

    let removedItems: SavedListingListItem[] = [];
    setSavedListings(prev => {
      removedItems = prev.filter(item => Number(item.listingId) === Number(listingId));
      return prev.filter(item => Number(item.listingId) !== Number(listingId));
    });

    const [, error] = await withTryAsync<{ data: Record<string, unknown> }, ErrorResponse>(endpoint)(API_V4_BASE_URL, 'DELETE', {
      listingId,
      source: themeName,
    });

    if (error) {
      console.error('Failed to remove saved listing:', error);
      if (removedItems.length > 0) {
        setSavedListings(prev => {
          const restored = [...prev];
          for (const item of removedItems) {
            if (!restored.some(existing => Number(existing.listingId) === Number(item.listingId))) {
              restored.push(item);
            }
          }
          return restored;
        });
      }
    } else {
      // Kinesis: only send when we have valid identifiers to avoid malformed events
      const hasValidIds = listingId != null && String(slugBeforeRemove ?? '').trim().length > 0;
      if (hasValidIds) {
        AnalyticsController.trackPropertyFavourited(
          {
            listing_id: String(listingId),
            address_slug: slugBeforeRemove as string,
          },
          {
            action: PROPERTY_FAVOURITED_ACTION_REMOVED,
            source: options?.source ?? PROPERTY_FAVOURITED_SOURCE_OTHER,
            url: asPath,
            tenant_id: toBedrockTenantId(tenantId),
          }
        );
      }
    }
  };

  const getSavedListings = async (params: SavedListingsParams) => {
    const paramsWithSource = {
      ...params,
      filter: {
        ...params.filter,
        source: themeName,
      },
    };

    const [response, error] = await withTryAsync<NestedSavedListingsResponse, ErrorResponse>(endpoint)(API_V4_BASE_URL, 'GET', paramsWithSource);

    if (error) {
      console.error('Failed to get saved listings:', error);
      return { savedListings: [], meta: error.meta || {}, error };
    } else if (response?.data) {
      return { savedListings: buildSavedListingsFull(response.data), meta: response.meta };
    } else {
      console.warn('Received successful response for getSavedListings, but no data found.');
      return { savedListings: [], meta: response?.meta || {}};
    }
  };

  const isSavedListing = (addressSlug?: string, listingId?: number): boolean => {
    if (addressSlug && savedListingSlugMap.has(addressSlug)) {
      return true;
    }
    if (listingId != null) {
      const id = Number(listingId);
      return Number.isFinite(id) && savedListingIdSet.has(id);
    }
    return false;
  };

  const mergeSavedListings: ISavedListingContext['mergeSavedListings'] = items => {
    if (items.length === 0) return;

    setSavedListings(previous => {
      const knownListingIds = new Set(previous.map(item => Number(item.listingId)));
      const additions = items
        // The embedded listing's id when there is one. Cards ask about the
        // listing they render, which is the embed, so a relisted favourite
        // would otherwise read as unfavourited.
        .map(item => ({ addressSlug: item.addressSlug, listingId: Number(item.listing?.id ?? item.listingId) }))
        .filter(item => !knownListingIds.has(item.listingId));
      return additions.length > 0 ? [...previous, ...additions] : previous;
    });
  };

  return (
    <SavedListingContext.Provider
      value={{
        savedListings,
        addSavedListing,
        removeSavedListing,
        getSavedListings,
        isSavedListing,
        mergeSavedListings,
      }}
    >
      {children}
    </SavedListingContext.Provider>
  );
}
